import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, FileText, History, LoaderCircle, Printer, Sparkles, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { COURRIERS } from "@/data/courriers";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const today = () => new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

function buildLetterHtml(user, courrier) {
  const paragraphs = courrier.body(user).map((p) => `<p>${p}</p>`).join("");
  return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>${courrier.title}</title>
  <style>body{font-family:Georgia,serif;max-width:700px;margin:40px auto;color:#111;line-height:1.7;font-size:14px}
  .sender{margin-bottom:28px}.recipient{text-align:right;margin-bottom:28px}.date{text-align:right;margin-bottom:24px}
  .objet{font-weight:bold;margin-bottom:24px}p{margin:0 0 14px}.signature{margin-top:40px;text-align:right;font-weight:bold}</style></head><body>
  <div class="sender">${user.name}<br/>${user.address}<br/>${user.postal_code}<br/>Tél. : ${user.phone}<br/>${user.email}</div>
  <div class="recipient">Préfecture ${user.departement ? `de ${user.departement.prefecture} — ${user.departement.name} (${user.departement.code})` : ""}<br/>Service des étrangers</div>
  <div class="date">Le ${today()}</div>
  <div class="objet">Objet : ${courrier.objet}</div>
  ${paragraphs}
  <div class="signature">${user.name}</div>
  </body></html>`;
}

export function CourriersSection() {
  const { user } = useAuth();
  const [open, setOpen] = useState(null);
  const [iaOpen, setIaOpen] = useState(false);
  const [situation, setSituation] = useState("");
  const [generating, setGenerating] = useState(false);
  const [iaLetter, setIaLetter] = useState(null);
  const [saved, setSaved] = useState([]);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const hasCoords = Boolean(user?.address && user?.postal_code && user?.phone);

  const loadSaved = () => api.get("/courriers").then((r) => setSaved(r.data)).catch(() => {});

  const downloadPdf = async (c) => {
    try {
      const res = await api.get(`/courriers/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${c.objet.replace(/[^\w\- ]/g, "").slice(0, 60) || "courrier"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF téléchargé — prêt à imprimer.");
    } catch {
      toast.error("Impossible de générer le PDF.");
    }
  };
  useEffect(() => { loadSaved(); }, []);

  const deleteSaved = async (c) => {
    try {
      await api.delete(`/courriers/${c.id}`);
      setSaved((s) => s.filter((x) => x.id !== c.id));
      toast.success("Courrier supprimé.");
    } catch {
      toast.error("Impossible de supprimer ce courrier.");
    }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setSavingEdit(true);
    try {
      const { data } = await api.patch(`/courriers/${editing.id}`, {
        objet: editing.objet,
        paragraphs: editing.text.split("\n").map((p) => p.trim()).filter(Boolean),
      });
      setSaved((s) => s.map((x) => (x.id === data.id ? data : x)));
      setEditing(null);
      toast.success("Courrier mis à jour.");
    } catch {
      toast.error("Impossible d'enregistrer les modifications.");
    } finally {
      setSavingEdit(false);
    }
  };

  const generateIa = async (e) => {
    e.preventDefault();
    if (situation.trim().length < 10) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/courriers/generate", { situation: situation.trim() });
      setIaLetter(data);
      loadSaved();
    } catch (err) {
      if (err.response?.status === 402) {
        toast.error(err.response.data.detail, {
          action: { label: "Passer à Premium", onClick: () => (window.location.href = "/dashboard/parametres") },
        });
      } else toast.error("Impossible de générer le courrier. Veuillez réessayer.");
    } finally {
      setGenerating(false);
    }
  };

  const closeIa = () => { setIaOpen(false); setIaLetter(null); setSituation(""); };

  const printLetter = (courrier) => {
    const win = window.open("", "_blank");
    win.document.write(buildLetterHtml(user, courrier));
    win.document.close();
    win.focus();
    win.print();
  };

  return (
    <section data-testid="courriers-section">
      <h2 className="mb-1 text-xs font-bold uppercase tracking-wider text-slate-400">Modèles de courrier</h2>
      <p className="mb-3 text-xs text-slate-500">
        Courriers administratifs pré-remplis avec votre adresse et votre préfecture
        {user?.departement ? ` (${user.departement.prefecture})` : ""}, prêts à imprimer.
      </p>
      {!hasCoords && (
        <p className="card-integra mb-3 border-amber-200 bg-amber-50 p-4 text-xs font-medium text-amber-700" data-testid="courriers-missing-coords">
          Complétez votre adresse, votre code postal et votre téléphone dans{" "}
          <Link to="/dashboard/parametres" className="font-bold underline">Paramètres</Link> pour générer vos courriers pré-remplis.
        </p>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        {COURRIERS.map((c) => (
          <button
            key={c.id}
            onClick={() => hasCoords && setOpen(c)}
            disabled={!hasCoords}
            className="card-integra card-hover flex items-start gap-3 p-4 text-left disabled:cursor-not-allowed disabled:opacity-60"
            data-testid={`courrier-card-${c.id}`}
          >
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bleu-light"><FileText className="h-4 w-4 text-bleu" /></span>
            <span>
              <span className="block text-[13px] font-bold text-slate-900">{c.title}</span>
              <span className="mt-0.5 block text-[11px] leading-relaxed text-slate-500">{c.desc}</span>
            </span>
          </button>
        ))}
        <button
          onClick={() => hasCoords && setIaOpen(true)}
          disabled={!hasCoords}
          className="card-integra card-hover flex items-start gap-3 border-bleu/20 bg-bleu-light/40 p-4 text-left ring-1 ring-bleu/10 disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
          data-testid="courrier-card-ia"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bleu"><Sparkles className="h-4 w-4 text-white" /></span>
          <span>
            <span className="block text-[13px] font-bold text-slate-900">Courrier sur mesure (IA)</span>
            <span className="mt-0.5 block text-[11px] leading-relaxed text-slate-500">
              Décrivez votre situation et l'Assistant IA rédige un courrier personnalisé, pré-rempli avec vos coordonnées et votre préfecture.
              {user?.plan !== "premium" && " (3/jour en compte gratuit)"}
            </span>
          </span>
        </button>
      </div>

      {saved.length > 0 && (
        <div className="mt-5" data-testid="courriers-saved-section">
          <h3 className="mb-2.5 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
            <History className="h-3.5 w-3.5" /> Mes courriers enregistrés ({saved.length})
          </h3>
          <ul className="space-y-2">
            {saved.map((c) => (
              <li key={c.id} className="card-integra flex items-center gap-3.5 p-4" data-testid={`courrier-saved-${c.id}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-bleu-light"><FileText className="h-4 w-4 text-bleu" /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-bold text-slate-900">{c.objet}</p>
                  <p className="text-[11px] text-slate-400">{new Date(c.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
                </div>
                <button onClick={() => setEditing({ id: c.id, objet: c.objet, text: c.paragraphs.join("\n\n"), paragraphs: c.paragraphs })}
                  className="rounded-full border border-slate-200 px-3.5 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:border-bleu/40 hover:text-bleu"
                  data-testid={`courrier-saved-open-${c.id}`}>
                  Ouvrir / Modifier
                </button>
                <button onClick={() => downloadPdf(c)}
                  className="text-slate-400 transition-colors hover:text-bleu" aria-label="Télécharger en PDF" data-testid={`courrier-saved-pdf-${c.id}`}>
                  <Download className="h-4 w-4" />
                </button>
                <button onClick={() => printLetter({ title: c.objet, objet: c.objet, body: () => c.paragraphs })}
                  className="text-slate-400 transition-colors hover:text-bleu" aria-label="Imprimer" data-testid={`courrier-saved-print-${c.id}`}>
                  <Printer className="h-4 w-4" />
                </button>
                <button onClick={() => deleteSaved(c)} className="text-slate-300 transition-colors hover:text-rouge" aria-label="Supprimer" data-testid={`courrier-saved-delete-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={() => setEditing(null)} data-testid="courrier-edit-modal">
          <form onSubmit={saveEdit} className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-display text-lg font-extrabold text-slate-900">Modifier le courrier</h3>
              <button type="button" onClick={() => setEditing(null)} aria-label="Fermer" className="text-slate-400 hover:text-slate-700" data-testid="courrier-edit-close"><X className="h-5 w-5" /></button>
            </div>
            <label className="mt-4 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Objet</label>
            <input value={editing.objet} onChange={(e) => setEditing((x) => ({ ...x, objet: e.target.value }))} required minLength={3}
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-bleu focus:outline-none" data-testid="courrier-edit-objet" />
            <label className="mt-3 block text-[11px] font-bold uppercase tracking-wider text-slate-400">Corps du courrier (un paragraphe par ligne)</label>
            <textarea value={editing.text} onChange={(e) => setEditing((x) => ({ ...x, text: e.target.value }))} rows={12}
              className="mt-1 w-full rounded-xl border border-slate-200 px-4 py-3 font-serif text-[13px] leading-relaxed focus:border-bleu focus:outline-none" data-testid="courrier-edit-text" />
            <div className="mt-4 flex gap-2">
              <button type="submit" disabled={savingEdit} className="btn-primary flex-1 !py-3 disabled:opacity-50" data-testid="courrier-edit-save">
                {savingEdit ? <LoaderCircle className="h-4 w-4 animate-spin" /> : "Enregistrer"}
              </button>
              <button type="button" onClick={() => printLetter({ title: editing.objet, objet: editing.objet, body: () => editing.text.split("\n").map((p) => p.trim()).filter(Boolean) })}
                className="btn-secondary !px-5 !py-3" data-testid="courrier-edit-print">
                <Printer className="h-4 w-4" /> Imprimer
              </button>
            </div>
          </form>
        </div>
      )}

      {iaOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={closeIa} data-testid="courrier-ia-modal">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <h3 className="flex items-center gap-2 font-display text-lg font-extrabold text-slate-900"><Sparkles className="h-4.5 w-4.5 text-bleu" /> Courrier sur mesure (IA)</h3>
              <button onClick={closeIa} aria-label="Fermer" className="text-slate-400 hover:text-slate-700" data-testid="courrier-ia-close"><X className="h-5 w-5" /></button>
            </div>
            {!iaLetter ? (
              <form onSubmit={generateIa} className="mt-4">
                <textarea
                  value={situation}
                  onChange={(e) => setSituation(e.target.value)}
                  rows={5}
                  placeholder="Décrivez votre situation… Ex : « Mon récépissé expire dans 3 semaines et je n'arrive pas à obtenir de rendez-vous pour le renouveler. Je travaille en CDI depuis 2 ans. »"
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm placeholder:text-slate-400 focus:border-bleu focus:outline-none focus:ring-2 focus:ring-bleu/15"
                  data-testid="courrier-ia-input"
                />
                <button type="submit" disabled={generating || situation.trim().length < 10} className="btn-primary mt-3 w-full !py-3 disabled:opacity-50" data-testid="courrier-ia-generate">
                  {generating ? <><LoaderCircle className="h-4 w-4 animate-spin" /> Rédaction en cours…</> : <><Sparkles className="h-4 w-4" /> Rédiger mon courrier</>}
                </button>
              </form>
            ) : (
              <>
                <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-5 font-serif text-[13px] leading-relaxed text-slate-800" data-testid="courrier-ia-preview">
                  <p className="font-semibold">{user.name}<br />{user.address}<br />{user.postal_code}<br />Tél. : {user.phone}<br />{user.email}</p>
                  <p className="mt-4 text-right font-semibold">
                    Préfecture {user.departement ? `de ${user.departement.prefecture} — ${user.departement.name} (${user.departement.code})` : ""}<br />Service des étrangers
                  </p>
                  <p className="mt-4 text-right">Le {today()}</p>
                  <p className="mt-4 font-bold">Objet : {iaLetter.objet}</p>
                  {iaLetter.paragraphs.map((p, i) => <p key={i} className="mt-3">{p}</p>)}
                  <p className="mt-6 text-right font-bold">{user.name}</p>
                </div>
                <div className="mt-4 flex gap-2">
                  <button onClick={() => printLetter({ title: iaLetter.objet, objet: iaLetter.objet, body: () => iaLetter.paragraphs })} className="btn-primary flex-1 !py-3" data-testid="courrier-ia-print">
                    <Printer className="h-4 w-4" /> Imprimer / PDF
                  </button>
                  <button onClick={() => setIaLetter(null)} className="rounded-full border border-slate-200 px-5 text-xs font-semibold text-slate-500" data-testid="courrier-ia-retry">Reformuler</button>
                </div>
                <p className="mt-2 text-center text-[10px] text-slate-400">Courrier généré par IA à relire et adapter avant envoi — vérifiez les informations avec les sources officielles.</p>
              </>
            )}
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={() => setOpen(null)} data-testid="courrier-modal">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <h3 className="font-display text-lg font-extrabold text-slate-900">{open.title}</h3>
              <button onClick={() => setOpen(null)} aria-label="Fermer" className="text-slate-400 hover:text-slate-700" data-testid="courrier-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-5 font-serif text-[13px] leading-relaxed text-slate-800" data-testid="courrier-preview">
              <p className="font-semibold">{user.name}<br />{user.address}<br />{user.postal_code}<br />Tél. : {user.phone}<br />{user.email}</p>
              <p className="mt-4 text-right font-semibold">
                Préfecture {user.departement ? `de ${user.departement.prefecture} — ${user.departement.name} (${user.departement.code})` : ""}<br />Service des étrangers
              </p>
              <p className="mt-4 text-right">Le {today()}</p>
              <p className="mt-4 font-bold">Objet : {open.objet}</p>
              {open.body(user).map((p, i) => <p key={i} className="mt-3">{p}</p>)}
              <p className="mt-6 text-right font-bold">{user.name}</p>
            </div>
            <button onClick={() => printLetter(open)} className="btn-primary mt-5 w-full !py-3" data-testid="courrier-print-button">
              <Printer className="h-4 w-4" /> Imprimer / Enregistrer en PDF
            </button>
            <p className="mt-2 text-center text-[10px] text-slate-400">Modèle indicatif à adapter à votre situation — vérifiez les pièces demandées par votre préfecture.</p>
          </div>
        </div>
      )}
    </section>
  );
}
